import { useEffect, useRef } from "react";

// ── スキャンライン：スクロール方向に合わせて光の帯が画面を走って消える ──

interface ScanLine {
  y: number;          // 現在のY位置
  direction: 1 | -1; // 1=下向き, -1=上向き
  opacity: number;
  life: number;
  maxLife: number;
}

export function ScrollParticles() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const scrollRef = useRef(0);
  const lastScrollRef = useRef(0);
  const linesRef = useRef<ScanLine[]>([]);
  const animRef = useRef<number>(0);
  const cooldownRef = useRef(0); // スポーン間隔制御

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    const onScroll = () => { scrollRef.current = window.scrollY; };
    window.addEventListener("scroll", onScroll, { passive: true });

    const spawnScanLine = (direction: 1 | -1) => {
      // スクロール方向の端から発生
      const startY = direction === 1 ? -8 : canvas.height + 8;
      linesRef.current.push({
        y: startY,
        direction,
        opacity: Math.random() * 0.15 + 0.25, // 0.25〜0.40（薄め）
        life: 0,
        maxLife: 50, // 約0.83秒@60fps
      });
      // 上限3本
      if (linesRef.current.length > 3) {
        linesRef.current = linesRef.current.slice(-3);
      }
    };

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const scrollSpeed = scrollRef.current - lastScrollRef.current;
      lastScrollRef.current = scrollRef.current;

      // クールダウン中は生成しない（連続スクロールでも間引く）
      cooldownRef.current = Math.max(0, cooldownRef.current - 1);

      if (Math.abs(scrollSpeed) > 3 && cooldownRef.current === 0) {
        const dir = scrollSpeed > 0 ? 1 : -1;
        spawnScanLine(dir);
        cooldownRef.current = 120; // 約2秒間隔
      }

      linesRef.current = linesRef.current.filter((l) => {
        l.life += 1;
        if (l.life > l.maxLife) return false;

        // 移動速度：画面高さ / maxLife で1周する速さ
        const speed = (canvas.height + 16) / l.maxLife;
        l.y += l.direction * speed;

        const progress = l.life / l.maxLife;
        // 最初と最後に素早くフェード
        const fade =
          progress < 0.15 ? progress / 0.15 :
          progress > 0.75 ? (1 - progress) / 0.25 :
          1;
        const alpha = l.opacity * fade;

        ctx.save();
        ctx.globalAlpha = alpha;

        // ── メインの光の帯（グラデーション） ──────────────────────
        const bandHeight = 10; // 太く
        const grad = ctx.createLinearGradient(0, l.y - bandHeight * 3, 0, l.y + bandHeight * 3);
        grad.addColorStop(0,   "hsla(190, 90%, 75%, 0)");
        grad.addColorStop(0.3, "hsla(190, 85%, 80%, 0.35)");
        grad.addColorStop(0.5, "hsla(195, 95%, 90%, 0.85)"); // 中心：やや薄め
        grad.addColorStop(0.7, "hsla(190, 85%, 80%, 0.35)");
        grad.addColorStop(1,   "hsla(190, 90%, 75%, 0)");

        ctx.fillStyle = grad;
        ctx.fillRect(0, l.y - bandHeight * 3, canvas.width, bandHeight * 6);

        // ── 外側の広いグロー（空気感） ────────────────────────────
        const glowGrad = ctx.createLinearGradient(0, l.y - 60, 0, l.y + 60);
        glowGrad.addColorStop(0,   "hsla(190, 80%, 70%, 0)");
        glowGrad.addColorStop(0.5, "hsla(190, 80%, 70%, 0.06)");
        glowGrad.addColorStop(1,   "hsla(190, 80%, 70%, 0)");

        ctx.fillStyle = glowGrad;
        ctx.fillRect(0, l.y - 60, canvas.width, 120);

        ctx.restore();
        return true;
      });

      animRef.current = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      cancelAnimationFrame(animRef.current);
      window.removeEventListener("resize", resize);
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 z-[1] pointer-events-none"
    />
  );
}
